import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Tooltip,
  Fab,
  useTheme,
  useMediaQuery,
  Drawer,
  Chip,
  Badge,
  Divider,
  Alert,
  Snackbar,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Checkbox,
  Menu,
  ListItemIcon,
  ListItemText,
  Pagination,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add,
  Search,
  FilterList,
  Refresh,
  Payment,
  CreditCard,
  PhoneAndroid,
  AccountBalance,
  Receipt,
  AccountBalanceWallet,
  Schedule,
  CardGiftcard,
  MoreHoriz,
  AttachMoney,
  Edit,
  Delete,
  Visibility,
  Close,
  Save,
  Cancel,
  CheckCircle,
  Warning,
  Info,
  MoreVert,
  ToggleOn,
  ToggleOff,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { 
  getPaymentModes, 
  createPaymentMode, 
  updatePaymentMode, 
  deletePaymentMode,
  togglePaymentModeStatus 
} from '../redux/actions/paymentModeActions';

const PaymentModeMaster = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const { paymentModes, pagination, loading, error } = useSelector((state) => state.paymentModes);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPaymentMode, setSelectedPaymentMode] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formDrawerOpen, setFormDrawerOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    is_active: true,
    requires_reference: false,
    requires_bank: false,
    sort_order: 0,
    icon: '',
    color: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  const paymentIcons = {
    'AttachMoney': <AttachMoney />,
    'CreditCard': <CreditCard />,
    'PhoneAndroid': <PhoneAndroid />,
    'AccountBalance': <AccountBalance />,
    'Receipt': <Receipt />,
    'AccountBalanceWallet': <AccountBalanceWallet />,
    'Schedule': <Schedule />,
    'CardGiftcard': <CardGiftcard />,
    'MoreHoriz': <MoreHoriz />,
  };

  useEffect(() => {
    dispatch(getPaymentModes({ page: currentPage, limit: 10, search: searchTerm, status: statusFilter }));
  }, [dispatch, searchTerm, statusFilter, currentPage]);

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const handleAddNew = () => {
    setSelectedPaymentMode(null);
    setIsEditing(true);
    setFormData({
      name: '',
      code: '',
      description: '',
      is_active: true,
      requires_reference: false,
      requires_bank: false,
      sort_order: 0,
      icon: '',
      color: '',
    });
    setFormErrors({});
    if (isMobile) {
      setFormDrawerOpen(true);
    }
  };

  const handleEdit = (paymentMode) => {
    setSelectedPaymentMode(paymentMode);
    setIsEditing(true);
    setFormData({
      name: paymentMode.name || '',
      code: paymentMode.code || '',
      description: paymentMode.description || '',
      is_active: paymentMode.is_active !== undefined ? paymentMode.is_active : true,
      requires_reference: paymentMode.requires_reference || false,
      requires_bank: paymentMode.requires_bank || false,
      sort_order: paymentMode.sort_order || 0,
      icon: paymentMode.icon || '',
      color: paymentMode.color || '',
    });
    setFormErrors({});
    if (isMobile) {
      setFormDrawerOpen(true);
    }
  };

  const handleView = (paymentMode) => {
    setSelectedPaymentMode(paymentMode);
    setIsEditing(false);
    setFormData({
      name: paymentMode.name || '',
      code: paymentMode.code || '',
      description: paymentMode.description || '',
      is_active: paymentMode.is_active !== undefined ? paymentMode.is_active : true,
      requires_reference: paymentMode.requires_reference || false,
      requires_bank: paymentMode.requires_bank || false,
      sort_order: paymentMode.sort_order || 0,
      icon: paymentMode.icon || '',
      color: paymentMode.color || '',
    });
    if (isMobile) {
      setFormDrawerOpen(true);
    }
  };

  const handleDelete = async (paymentMode) => {
    if (window.confirm(`Are you sure you want to delete ${paymentMode.name}?`)) {
      const result = await dispatch(deletePaymentMode(paymentMode.id));
      if (result.success) {
        setSnackbar({
          open: true,
          message: 'Payment mode deleted successfully',
          severity: 'success'
        });
        dispatch(getPaymentModes({ page: currentPage, limit: 10, search: searchTerm, status: statusFilter }));
      } else {
        setSnackbar({
          open: true,
          message: result.message || 'Failed to delete payment mode',
          severity: 'error'
        });
      }
    }
  };

  const handleToggleStatus = async (paymentMode) => {
    const result = await dispatch(togglePaymentModeStatus(paymentMode.id));
    if (result.success) {
      setSnackbar({
        open: true,
        message: `Payment mode ${paymentMode.is_active ? 'deactivated' : 'activated'} successfully`,
        severity: 'success'
      });
      dispatch(getPaymentModes({ page: currentPage, limit: 10, search: searchTerm, status: statusFilter }));
    } else {
      setSnackbar({
        open: true,
        message: result.message || 'Failed to toggle payment mode status',
        severity: 'error'
      });
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.code.trim()) errors.code = 'Code is required';
    if (formData.sort_order && (isNaN(formData.sort_order) || formData.sort_order < 0)) {
      errors.sort_order = 'Sort order must be a valid positive number';
    }
    
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const result = isEditing && selectedPaymentMode
      ? await dispatch(updatePaymentMode(selectedPaymentMode.id, formData))
      : await dispatch(createPaymentMode(formData));
    
    if (result.success) {
      setSnackbar({
        open: true,
        message: isEditing ? 'Payment mode updated successfully' : 'Payment mode created successfully',
        severity: 'success'
      });
      setFormDrawerOpen(false);
      setSelectedPaymentMode(null);
      setIsEditing(false);
      dispatch(getPaymentModes({ page: currentPage, limit: 10, search: searchTerm, status: statusFilter }));
    } else {
      setSnackbar({
        open: true,
        message: result.message || 'Operation failed',
        severity: 'error'
      });
    }
  };

  const handleFormCancel = () => {
    setFormDrawerOpen(false);
    setSelectedPaymentMode(null);
    setIsEditing(false);
    setFormData({
      name: '',
      code: '',
      description: '',
      is_active: true,
      requires_reference: false,
      requires_bank: false,
      sort_order: 0,
      icon: '',
      color: '',
    });
    setFormErrors({});
  };

  const handleRefresh = () => {
    dispatch(getPaymentModes({ page: currentPage, limit: 10, search: searchTerm, status: statusFilter }));
  };

  const getPaymentIcon = (iconName) => {
    return paymentIcons[iconName] || <Payment />;
  };

  const getStatusColor = (isActive) => {
    return isActive ? 'success' : 'default';
  };

  const getStatusLabel = (isActive) => {
    return isActive ? 'Active' : 'Inactive';
  };

  const getStats = () => {
    const total = paymentModes.length;
    const active = paymentModes.filter(p => p.is_active).length;
    const inactive = total - active;
    const withReference = paymentModes.filter(p => p.requires_reference).length;
    
    return { total, active, inactive, withReference };
  };

  const stats = getStats();

  const filteredPaymentModes = paymentModes.filter(paymentMode => {
    if (statusFilter === 'active') return paymentMode.is_active;
    if (statusFilter === 'inactive') return !paymentMode.is_active;
    return true;
  });

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', p: 2, gap: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Payment Mode Master
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage payment methods for billing
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} disabled={loading}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddNew}
            sx={{ borderRadius: 2 }}
          >
            Add Payment Mode
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.total}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total Payment Modes
                  </Typography>
                </Box>
                <Payment sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.active}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Active Modes
                  </Typography>
                </Box>
                <CheckCircle sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.inactive}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Inactive Modes
                  </Typography>
                </Box>
                <Warning sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.withReference}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Require Reference
                  </Typography>
                </Box>
                <Info sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        {/* Left Side - Payment Mode Grid */}
        <Grid item xs={12} md={7}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 0 }}>
              {/* Search and Filter Bar */}
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                  <TextField
                    fullWidth
                    placeholder="Search payment modes..."
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ maxWidth: 400 }}
                  />
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      label="Status"
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {pagination.totalItems} payment modes found
                  </Typography>
                  <Chip
                    label={`Page ${pagination.currentPage} of ${pagination.totalPages}`}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </Box>

              {/* Payment Mode List */}
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                    <Typography>Loading...</Typography>
                  </Box>
                ) : filteredPaymentModes.length === 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, p: 3 }}>
                    <Payment sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No payment modes found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" align="center">
                      {searchTerm ? 'Try adjusting your search terms' : 'Add your first payment mode to get started'}
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'grey.50' }}>
                          <TableCell>Payment Mode</TableCell>
                          <TableCell>Code</TableCell>
                          <TableCell>Requirements</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredPaymentModes.map((paymentMode) => (
                          <TableRow 
                            key={paymentMode.id}
                            hover
                            sx={{ 
                              cursor: 'pointer',
                              '&:hover': { backgroundColor: 'action.hover' }
                            }}
                            onClick={() => handleView(paymentMode)}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ 
                                  bgcolor: paymentMode.color || 'primary.main', 
                                  width: 40, 
                                  height: 40 
                                }}>
                                  {getPaymentIcon(paymentMode.icon)}
                                </Avatar>
                                <Box>
                                  <Typography variant="subtitle2" fontWeight="bold">
                                    {paymentMode.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {paymentMode.description}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={paymentMode.code}
                                size="small"
                                variant="outlined"
                                color="primary"
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {paymentMode.requires_reference && (
                                  <Chip label="Reference" size="small" color="info" variant="outlined" />
                                )}
                                {paymentMode.requires_bank && (
                                  <Chip label="Bank" size="small" color="warning" variant="outlined" />
                                )}
                                {!paymentMode.requires_reference && !paymentMode.requires_bank && (
                                  <Typography variant="caption" color="text.secondary">
                                    None
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={getStatusLabel(paymentMode.is_active)}
                                size="small"
                                color={getStatusColor(paymentMode.is_active)}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Tooltip title="Toggle Status">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleToggleStatus(paymentMode);
                                    }}
                                    color={paymentMode.is_active ? 'success' : 'default'}
                                  >
                                    {paymentMode.is_active ? <ToggleOn /> : <ToggleOff />}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEdit(paymentMode);
                                    }}
                                    color="primary"
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDelete(paymentMode);
                                    }}
                                    color="error"
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>

              {/* Pagination */}
              {!loading && filteredPaymentModes.length > 0 && pagination.totalPages > 1 && (
                <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'center' }}>
                  <Pagination
                    count={pagination.totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                    size="small"
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Side - Payment Mode Form */}
        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 0 }}>
              {/* Form Header */}
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      {isEditing ? 'Edit Payment Mode' : selectedPaymentMode ? 'Payment Mode Details' : 'Add New Payment Mode'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {isEditing ? 'Update payment mode information' : selectedPaymentMode ? 'View payment mode details' : 'Enter payment mode information'}
                    </Typography>
                  </Box>
                  {selectedPaymentMode && (
                    <IconButton onClick={handleFormCancel} size="small">
                      <Close />
                    </IconButton>
                  )}
                </Box>
              </Box>

              {/* Form Content */}
              <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <form onSubmit={handleFormSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Payment Mode Name"
                        name="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        error={!!formErrors.name}
                        helperText={formErrors.name}
                        disabled={!isEditing && selectedPaymentMode}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Payment color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Code"
                        name="code"
                        value={formData.code}
                        onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                        error={!!formErrors.code}
                        helperText={formErrors.code}
                        disabled={!isEditing && selectedPaymentMode}
                        required
                        placeholder="e.g., CASH, CARD, UPI"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Description"
                        name="description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        disabled={!isEditing && selectedPaymentMode}
                        multiline
                        rows={2}
                        placeholder="Brief description of the payment mode"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Icon Name"
                        name="icon"
                        value={formData.icon}
                        onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                        disabled={!isEditing && selectedPaymentMode}
                        placeholder="e.g., AttachMoney, CreditCard"
                        helperText="Material-UI icon name"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Color"
                        name="color"
                        value={formData.color}
                        onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                        disabled={!isEditing && selectedPaymentMode}
                        placeholder="e.g., #4CAF50, #2196F3"
                        helperText="Hex color code"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Sort Order"
                        name="sort_order"
                        type="number"
                        value={formData.sort_order}
                        onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                        error={!!formErrors.sort_order}
                        helperText={formErrors.sort_order}
                        disabled={!isEditing && selectedPaymentMode}
                        inputProps={{ min: 0 }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.is_active}
                              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                              disabled={!isEditing && selectedPaymentMode}
                            />
                          }
                          label="Active"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.requires_reference}
                              onChange={(e) => setFormData({ ...formData, requires_reference: e.target.checked })}
                              disabled={!isEditing && selectedPaymentMode}
                            />
                          }
                          label="Requires Reference Number"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.requires_bank}
                              onChange={(e) => setFormData({ ...formData, requires_bank: e.target.checked })}
                              disabled={!isEditing && selectedPaymentMode}
                            />
                          }
                          label="Requires Bank Details"
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </form>
              </Box>

              {/* Form Actions */}
              {isEditing && (
                <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      startIcon={<Cancel />}
                      onClick={handleFormCancel}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Save />}
                      onClick={handleFormSubmit}
                    >
                      {selectedPaymentMode ? 'Update' : 'Create'}
                    </Button>
                  </Box>
                </Box>
              )}

              {!isEditing && selectedPaymentMode && (
                <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      startIcon={<Edit />}
                      onClick={() => setIsEditing(true)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<Delete />}
                      onClick={() => handleDelete(selectedPaymentMode)}
                    >
                      Delete
                    </Button>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Mobile Form Drawer */}
      <Drawer
        anchor="bottom"
        open={formDrawerOpen && isMobile}
        onClose={handleFormCancel}
        sx={{
          '& .MuiDrawer-paper': {
            height: '80vh',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              {isEditing ? 'Edit Payment Mode' : selectedPaymentMode ? 'Payment Mode Details' : 'Add New Payment Mode'}
            </Typography>
            <IconButton onClick={handleFormCancel}>
              <Close />
            </IconButton>
          </Box>
          {/* Mobile form content would go here */}
        </Box>
      </Drawer>

      {/* Floating Action Button for Mobile */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="add"
          onClick={handleAddNew}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1000,
          }}
        >
          <Add />
        </Fab>
      )}

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PaymentModeMaster;
